"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import {
  buildUkNotFoundRecoveryEvent,
  resolveUkRecovery,
  type NotFoundRecoveryEventCallback,
} from "@/lib/notFoundRecovery";

type NotFoundRecoveryActionsProps = {
  onRecoveryEvent?: NotFoundRecoveryEventCallback;
};

const subscribeToHydration = () => () => undefined;
const getHydratedSnapshot = () => true;
const getServerSnapshot = () => false;

const goldButtonClass =
  "inline-flex min-h-12 items-center justify-center rounded-[12px] bg-[#d5a746] px-6 text-sm font-black text-[#102f2a] shadow-[0_16px_34px_rgba(155,107,25,0.2)] transition hover:-translate-y-0.5 hover:bg-[#e0b95b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#946516]";
const outlineButtonClass =
  "inline-flex min-h-12 items-center justify-center rounded-[12px] border border-[#d1b98e] bg-white/64 px-6 text-sm font-black text-[#102f2a] shadow-[0_12px_28px_rgba(49,39,24,0.06)] transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#946516]";

/**
 * Batch E1 integration boundary. Production passes no callback, so W1-02 emits
 * no live analytics event until the governed registry supports these events.
 */
export function NotFoundRecoveryActions({ onRecoveryEvent }: NotFoundRecoveryActionsProps) {
  const routePathname = usePathname();
  const hydrated = useSyncExternalStore(subscribeToHydration, getHydratedSnapshot, getServerSnapshot);
  const pathname = hydrated ? routePathname : null;
  const recovery = resolveUkRecovery(pathname);
  const announcedViewKey = useRef<string | null>(null);

  const buildEvent = useCallback((action: "view" | "recovery_click", destination: string) => {
    return buildUkNotFoundRecoveryEvent({
      action,
      rawPath: pathname,
      destination,
      referrer: typeof document === "undefined" ? null : document.referrer,
      viewportWidth: typeof window === "undefined" ? 1024 : window.innerWidth,
    });
  }, [pathname]);

  useEffect(() => {
    if (!pathname || !onRecoveryEvent || announcedViewKey.current === pathname) return;
    announcedViewKey.current = pathname;
    onRecoveryEvent(buildEvent("view", recovery.href));
  }, [buildEvent, onRecoveryEvent, pathname, recovery.href]);

  return (
    <>
      <Link
        href={recovery.href}
        data-not-found-recovery="primary"
        className={goldButtonClass}
        onClick={() => onRecoveryEvent?.(buildEvent("recovery_click", recovery.href))}
      >
        {recovery.label}
      </Link>
      <Link
        href="/contact"
        data-not-found-recovery="contact"
        className={outlineButtonClass}
        onClick={() => onRecoveryEvent?.(buildEvent("recovery_click", "/contact"))}
      >
        Ask a Question
      </Link>
      <span className="sr-only" role="status" aria-live="polite">
        Recommended local recovery: {recovery.label}.
      </span>
    </>
  );
}
